import React, { useRef, useState, useEffect } from 'react';
import './BusinessGallery.css';
// import { setMainBusinessPhoto } from '../../services/businessPhotoService';
import { deleteBusinessPhoto } from '../../services/deleteBusinessPhoto';
import { registerBusinessPhoto } from '../../services/registerBusinessPhoto';
import { setMainBusinessPhoto } from '../../services/setMainBusinessPhoto';
import { useAuth } from '../../contexts/AuthContext';


interface BusinessGalleryProps {
  businessId: string;
  initialPhotos: string[];
  onChange?: (urls: string[]) => void;
}

const MAX_FILES = 10;
const MAX_SIZE_MB = 20;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const BusinessGallery: React.FC<BusinessGalleryProps> = ({ businessId, initialPhotos, onChange }) => {
  // Always use backend proxy for upload (even in dev)
  const uploadUrl = '/api/photo/upload';
  const { user } = useAuth();
  const [photos, setPhotos] = useState<string[]>(initialPhotos);
  const [mainPhoto, setMainPhoto] = useState<string | null>(initialPhotos[0] || null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    //console.log('[BusinessGallery] initialPhotos:', initialPhotos);
    setPhotos(initialPhotos);
    setMainPhoto(initialPhotos[0] || null);
  }, [initialPhotos]);

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    setError(null);
    const newPhotos: string[] = [];
    if (photos.length + files.length > MAX_FILES) {
      setError(`Максимум ${MAX_FILES} фото.`);
      return;
    }
    setUploading(true);
    const token = localStorage.getItem('auth_token');
    let firstUploadedUrl: string | null = null;
    for (const [, file] of Array.from(files).entries()) {
        if (!ALLOWED_TYPES.includes(file.type)) {
          setError('Дозволено лише JPG/PNG/WebP.');
          setUploading(false);
          return;
        }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        setError(`Максимальний розмір ${MAX_SIZE_MB}MB.`);
        setUploading(false);
        return;
      }
      // Завантаження через photo-service
      const formData = new FormData();
      formData.append('file', file);
      // Add owner ID and business ID as prefix for folder organization: ownerId/businessId/
      if (user?.id) {
        formData.append('prefix', `${user.id}/${businessId}`);
      }
      try {
        const res = await fetch(uploadUrl, {
          method: 'POST',
          body: formData,
        });
        if (!res.ok) {
          setError('Помилка завантаження: ' + res.statusText);
          setUploading(false);
          return;
        }
        const data = await res.json();
        if (!data.url) {
          setError('Помилка: не отримано url');
          setUploading(false);
          return;
        }
        newPhotos.push(data.url);
        if (firstUploadedUrl === null) firstUploadedUrl = data.url;
        // Реєстрація фото у БД після upload
        try {
          await registerBusinessPhoto(businessId, data.url, token || undefined);
        } catch (e) {
          setError('Помилка реєстрації фото у БД');
          setUploading(false);
          return;
        }
      } catch (e) {
        setError('Помилка мережі при завантаженні');
        setUploading(false);
        return;
      }
    }
    const allPhotos = [...photos, ...newPhotos];
    setPhotos(allPhotos);
    setUploading(false);
    // Якщо це перше фото (до upload не було жодного), зробити його головним
    if (photos.length === 0 && firstUploadedUrl) {
      try {
        await setMainBusinessPhoto(businessId, firstUploadedUrl, token || undefined);
      } catch (e) {
        // ignore error
      }
    }
    onChange?.(allPhotos);
  };

  const handleDelete = async (url: string) => {
    setError(null);
    try {
      const token = localStorage.getItem('auth_token') || undefined;
      await deleteBusinessPhoto(businessId, url, token);
    } catch (e) {
      setError('Помилка видалення фото з бази або файлової системи');
      return;
    }
    const updated = photos.filter((p) => p !== url);
    setPhotos(updated);
    // Якщо видалене фото було головним і залишилось ще фото — зробити нове перше фото головним
    if (url === mainPhoto && updated.length > 0) {
      try {
        const token = localStorage.getItem('auth_token') || undefined;
        await setMainBusinessPhoto(businessId, updated[0], token);
        setMainPhoto(updated[0]);
      } catch (e) {
        // ignore error
      }
    } else if (updated.length === 0) {
      setMainPhoto(null);
    }
    onChange?.(updated);
  };

  const handleSetMain = async (url: string) => {
    try {
      setMainPhoto(url);
      const token = localStorage.getItem('auth_token');
      await setMainBusinessPhoto(businessId, url, token || undefined);
      // (опціонально) можна оновити photos з бекенду, якщо потрібно
    } catch (e) {
      setError('Не вдалося встановити головне фото');
    }
  };

  return (
    <div>
      <div className="business-gallery">
        {photos.map((url) => (
          <div key={url} className="business-gallery-photo">
            <img
              src={url}
              alt="business"
              className={
                'business-gallery-img' + (url === mainPhoto ? ' business-gallery-img-main' : '')
              }
            />
            <button
              className="business-gallery-delete-btn"
              onClick={() => handleDelete(url)}
              disabled={uploading}
              title="Видалити"
            >
              ×
            </button>
            <button
              className={
                'business-gallery-setmain-btn' + (url === mainPhoto ? ' main' : '')
              }
              onClick={() => handleSetMain(url)}
              disabled={uploading || url === mainPhoto}
              title="Зробити головним"
            >
              {url === mainPhoto ? 'Головне' : 'Зробити головним'}
            </button>
          </div>
        ))}
        {photos.length < MAX_FILES && (
          <div
            className="business-gallery-upload"
            onClick={() => inputRef.current?.click()}
            onDrop={e => {
              e.preventDefault();
              handleFiles(e.dataTransfer.files);
            }}
            onDragOver={e => e.preventDefault()}
          >
            {uploading ? 'Завантаження...' : '+ Фото'}
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={e => handleFiles(e.target.files)}
              disabled={uploading}
            />
          </div>
        )}
      </div>
      {error && <div className="business-gallery-error">{error}</div>}
    </div>
  );
};
